/**
 * FERP Portal - Scope Dimensions Hook
 *
 * DRR-178: Backend-driven scope dimensions replace hardcoded GROUPABLE_SYSTEM_FIELDS.
 *
 * Reads available grouping dimensions from the Setting config (via GraphQL) and
 * returns them for a given primitive type. Each dimension includes a suggested
 * visual renderer (table, card, kanban, spatial, timeline, graph, master_detail).
 *
 * Labels are pre-resolved by the backend using {subject_label} and {scope_labels.*}
 * from the active Setting (e.g., "By Resident" for ALF, "By Client" for home care).
 *
 * Usage:
 * ```tsx
 * const { dimensions, loading } = useScopeDimensions('task');
 * // dimensions: [{ dimensionId: 'subject_actor', label: 'By Resident', suggestedRenderer: 'master_detail', ... }]
 * ```
 */

import { useMemo } from 'react';
import { useGetAvailableScopeDimensionsQuery } from '@/generated/graphql';
import { GROUPABLE_SYSTEM_FIELDS } from '@/utils/tabulatorConfig';

export interface ScopeDimension {
  dimensionId: string;
  field: string;
  label: string;
  suggestedRenderer: string;
  availableRenderers: string[];
  sortOrder: number;
}

export interface UseScopeDimensionsReturn {
  /** Backend-driven scope dimensions for this primitive type */
  dimensions: ScopeDimension[];
  /** Whether the query is loading */
  loading: boolean;
  /**
   * Fallback groupable fields for when backend dimensions aren't available.
   * Merges system fields + schema-derived fields. Use dimensions when available.
   */
  fallbackFields: ReadonlyArray<{ field: string; label: string }>;
}

export function useScopeDimensions(primitiveType: string): UseScopeDimensionsReturn {
  const { data, loading } = useGetAvailableScopeDimensionsQuery({
    variables: { primitiveType },
    fetchPolicy: 'cache-first',
  });

  const dimensions = useMemo<ScopeDimension[]>(() => {
    const raw = data?.availableScopeDimensions;
    if (!raw || !Array.isArray(raw)) return [];

    return raw
      .map((dim) => ({
        dimensionId: dim.dimensionId,
        field: dim.field,
        label: dim.label,
        suggestedRenderer: dim.suggestedRenderer,
        availableRenderers: [...dim.availableRenderers],
        sortOrder: dim.sortOrder,
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [data]);

  return {
    dimensions,
    loading,
    fallbackFields: GROUPABLE_SYSTEM_FIELDS,
  };
}
